import {
  createAsyncThunk,
  createEntityAdapter,
  createSlice,
  PayloadAction
} from '@reduxjs/toolkit';

import { serviceGetEvents } from '../services';

import {
  EventActionTarget,
  EventModel,
  PaginationRequestModel
} from '../models';

interface BackendEventModel extends EventModel {
  backendName: string;
  backendUrl: string;
  _entityId: string;
}

const fetchEvents = createAsyncThunk(
  'event/fetch',
  async (
    data: PaginationRequestModel,
    thunkAPI
  ) => {
    return await serviceGetEvents(
      data.parameters,
      data.limit,
      data.page,
      data.orderBy,
      data.direction
    );
  }
);

/*
 * IMPORTANT:
 *
 * Event ID nije dovoljan jer PET i S1 mogu imati isti ID.
 *
 * Zato Redux koristi:
 *
 * PET:123
 * S1:123
 *
 * kao dva različita entity ID-a.
 */
const eventEntityAdapter =
  createEntityAdapter<BackendEventModel>({
    selectId: (event) =>
      event._entityId
  });

type EventSocketUpdate =
  Partial<EventModel> & {
    id: number;
    backendName?: string;
    backendUrl?: string;
  };

/*
 * Pronađi interni Redux ID eventa.
 *
 * Ako socket pošalje backendName:
 *
 *     PET + 123 -> PET:123
 *
 * Ako socket još uvijek ne šalje backendName,
 * pokušavamo pronaći event samo po originalnom ID-u.
 */
function findEventEntityId(
  state: any,
  socketEventId: number,
  backendName?: string
): string | undefined {
  /*
   * Najbrži slučaj.
   *
   * Ako imamo backendName, direktno
   * napravimo composite entity ID.
   */
  if (backendName) {
    const entityId =
      `${backendName}:${socketEventId}`;

    if (state.entities[entityId]) {
      return entityId;
    }
  }

  /*
   * Fallback za postojeće socket evente koji
   * još nemaju backendName.
   *
   * Ako isti ID postoji na više backenda,
   * ovaj fallback vraća prvi pronađeni.
   */
  return state.ids.find(
    (id: string) =>
      state.entities[id]?.id ===
      socketEventId
  );
}

/*
 * Primijeni JEDAN socket update.
 *
 * Ovo se i dalje koristi tamo gdje imamo
 * pojedinačni update.
 */
function applySocketUpdate(
  state: any,
  socketEvent: EventSocketUpdate
): void {
  const entityId =
    findEventEntityId(
      state,
      socketEvent.id,
      socketEvent.backendName
    );

  if (entityId === undefined) {
    return;
  }

  const {
    id: _socketEventId,
    backendName: _backendName,
    backendUrl: _backendUrl,
    ...changes
  } = socketEvent;

  eventEntityAdapter.updateOne(
    state,
    {
      id: entityId,
      changes
    }
  );
}

/*
 * Primijeni VIŠE socket update-a odjednom.
 *
 * Ovo je bitan performance fix.
 *
 * Umjesto:
 *
 *   updateOne()
 *   updateOne()
 *   updateOne()
 *   updateOne()
 *
 * koristimo:
 *
 *   updateMany()
 *
 * za cijeli socket paket.
 */
function applySocketUpdates(
  state: any,
  socketEvents: EventSocketUpdate[]
): void {
  if (!socketEvents.length) {
    return;
  }

  const updates = socketEvents
    .map((socketEvent) => {
      const entityId =
        findEventEntityId(
          state,
          socketEvent.id,
          socketEvent.backendName
        );

      if (entityId === undefined) {
        return null;
      }

      const {
        id: _socketEventId,
        backendName: _backendName,
        backendUrl: _backendUrl,
        ...changes
      } = socketEvent;

      return {
        id: entityId,
        changes
      };
    })
    .filter(
      (
        update
      ): update is {
        id: string;
        changes: Partial<BackendEventModel>;
      } =>
        update !== null
    );

  if (!updates.length) {
    return;
  }

  /*
   * Jedan updateMany umjesto N updateOne poziva.
   */
  eventEntityAdapter.updateMany(
    state,
    updates
  );
}

/*
 * Napravi ključ za pending socket update.
 *
 * PET:123
 * S1:123
 *
 * više ne mogu pregaziti jedan drugi.
 */
function getSocketUpdateKey(
  socketEvent: EventSocketUpdate
): string {
  if (socketEvent.backendName) {
    return `${socketEvent.backendName}:${socketEvent.id}`;
  }

  return `unknown:${socketEvent.id}`;
}

const eventsSlice = createSlice({
  name: 'eventManagement',

  initialState:
    eventEntityAdapter.getInitialState({
      status: 'idle',
      totalCount: 0,
      error: null,
      currentRequestId:
        null as string | null,

      /*
       * Composite key umjesto samo event.id.
       */
      pendingSocketUpdates:
        {} as Record<
          string,
          EventSocketUpdate
        >
    }),

  reducers: {
    /*
     * Ručni update jednog eventa.
     */
    updateEvent: (
      state,
      action: PayloadAction<{
        id: number;
        backendName: string;
        changes: Partial<BackendEventModel>;
      }>
    ) => {
      const {
        id,
        backendName,
        changes
      } = action.payload;

      const entityId =
        `${backendName}:${id}`;

      if (!state.entities[entityId]) {
        return;
      }

      eventEntityAdapter.updateOne(
        state,
        {
          id: entityId,
          changes
        }
      );
    },

    /*
     * Postojeći pojedinačni socket update.
     *
     * Ostavljamo ga zbog kompatibilnosti
     * sa eventualnim drugim mjestima u aplikaciji
     * koja ga već koriste.
     */
    updateEventFromSocket: (
      state,
      action: PayloadAction<EventSocketUpdate>
    ) => {
      const socketEvent =
        action.payload;

      /*
       * Ako se trenutno radi fetch,
       * sačuvaj socket update da se ne izgubi
       * kada fetch završi sa setAll().
       */
      if (state.status === 'pending') {
        const updateKey =
          getSocketUpdateKey(
            socketEvent
          );

        state.pendingSocketUpdates[
          updateKey
        ] = {
          ...state.pendingSocketUpdates[
            updateKey
          ],
          ...socketEvent
        };
      }

      applySocketUpdate(
        state,
        socketEvent
      );
    },

    /*
     * BATCH socket update.
     *
     * Ovo koristi EventsTable za cijeli
     * socket paket odjednom.
     *
     * Umjesto:
     *
     *   dispatch(updateEventFromSocket(event1))
     *   dispatch(updateEventFromSocket(event2))
     *   dispatch(updateEventFromSocket(event3))
     *
     * sada imamo:
     *
     *   dispatch(updateEventsFromSocket([
     *     event1,
     *     event2,
     *     event3
     *   ]))
     */
    updateEventsFromSocket: (
      state,
      action: PayloadAction<
        EventSocketUpdate[]
      >
    ) => {
      const socketEvents =
        action.payload;

      if (!socketEvents.length) {
        return;
      }

      /*
       * Ako se trenutno radi fetch,
       * zapamti najnoviji update za svaki
       * konkretan event.
       */
      if (state.status === 'pending') {
        socketEvents.forEach(
          (socketEvent) => {
            const updateKey =
              getSocketUpdateKey(
                socketEvent
              );

            state.pendingSocketUpdates[
              updateKey
            ] = {
              ...state.pendingSocketUpdates[
                updateKey
              ],
              ...socketEvent
            };
          }
        );
      }

      /*
       * KLJUČNI PERFORMANCE FIX:
       *
       * Jedan updateMany() za cijeli paket.
       */
      applySocketUpdates(
        state,
        socketEvents
      );
    },

    clearState: (state) => {
      eventEntityAdapter.removeAll(
        state
      );

      state.status = 'idle';
      state.totalCount = 0;
      state.currentRequestId = null;
      state.pendingSocketUpdates = {};
    }
  },

  extraReducers: (builder) => {
    /*
     * FETCH PENDING
     */
    builder.addCase(
      fetchEvents.pending,
      (state, action) => {
        state.status = 'pending';

        state.currentRequestId =
          action.meta.requestId;

        state.pendingSocketUpdates = {};
      }
    );

    /*
     * FETCH FULFILLED
     */
    builder.addCase(
      fetchEvents.fulfilled,
      (state, action) => {
        /*
         * Ignoriši rezultat starog requesta.
         */
        if (
          state.currentRequestId !==
          action.meta.requestId
        ) {
          return;
        }

        const pendingSocketUpdates =
          Object.values(
            state.pendingSocketUpdates
          ) as EventSocketUpdate[];

        state.status = 'fulfilled';
        state.error = null;

        state.totalCount =
          action.payload.rowcount;

        /*
         * serviceGetEvents() sada vraća:
         *
         * {
         *   id: 123,
         *   backendName: "PET",
         *   backendUrl: "...",
         *   ...
         * }
         *
         * Napravimo jedinstveni Redux ID:
         *
         * PET:123
         * S1:123
         */
        const events =
          action.payload.data.map(
            (event: any) => ({
              ...event,

              _entityId:
                `${event.backendName}:${event.id}`
            })
          );

        /*
         * Zamijeni trenutnu listu eventova.
         */
        eventEntityAdapter.setAll(
          state,
          events
        );

        /*
         * Socket update koji je stigao dok se
         * lista učitavala ponovo primijeni
         * nakon setAll().
         *
         * Pošto ih može biti više, koristimo
         * batch updateMany().
         */
        applySocketUpdates(
          state,
          pendingSocketUpdates
        );

        state.currentRequestId = null;
        state.pendingSocketUpdates = {};
      }
    );

    /*
     * FETCH REJECTED
     */
    builder.addCase(
      fetchEvents.rejected,
      (state, action) => {
        /*
         * Ignoriši rezultat starog requesta.
         */
        if (
          state.currentRequestId !==
          action.meta.requestId
        ) {
          return;
        }

        state.status = 'error';
        state.currentRequestId = null;
        state.pendingSocketUpdates = {};

        // @ts-ignore
        state.error = action.error;
      }
    );
  }
});

/*
 * Osnovni selector po internom Redux ID-u.
 */
const {
  selectById
} =
  eventEntityAdapter.getSelectors();

/*
 * Selector po ORIGINALNOM event ID-u.
 *
 * Primjer:
 *
 * PET:123
 * S1:123
 *
 * Ako pozovemo:
 *
 * selectEventById(state, 123)
 *
 * vraća prvi pronađeni event.
 *
 * Za tačan backend koristi
 * selectEventByBackend().
 */
const selectEventById = (
  state: any,
  eventId: number
) => {
  return state.ids
    .map(
      (id: string) =>
        state.entities[id]
    )
    .find(
      (event: BackendEventModel) =>
        event?.id === eventId
    );
};

/*
 * Selector za konkretan backend.
 *
 * Primjer:
 *
 * selectEventByBackend(
 *   state,
 *   123,
 *   'PET'
 * )
 *
 * -> PET:123
 *
 * selectEventByBackend(
 *   state,
 *   123,
 *   'S1'
 * )
 *
 * -> S1:123
 */
const selectEventByBackend = (
  state: any,
  eventId: number,
  backendName: string
) => {
  const entityId =
    `${backendName}:${eventId}`;

  return selectById(
    state,
    entityId
  );
};

/*
 * Svi eventi.
 */
const selectAllEvents = (
  state: any
): BackendEventModel[] => {
  return state.ids.map(
    (eventId: string) =>
      state.entities[eventId]
  );
};

/*
 * Svi INTERNAL entity ID-evi.
 *
 * Primjer:
 *
 * [
 *   "PET:123",
 *   "PET:456",
 *   "S1:123",
 *   "S1:789"
 * ]
 */
const selectAllEventIds = (
  state: any
): string[] => {
  return state.ids;
};

/*
 * Vrati ID-eve povezanih eventa.
 *
 * Ovdje zadržavamo originalni numeric
 * event ID, jer actionTargets koriste
 * backend + originalni ID.
 */
const selectRelatedEventIds = (
  state: any,
  eventId: number
): number[] => {
  const event =
    selectEventById(
      state,
      eventId
    );

  return event
    ? selectRelatedEventTargets(
        state,
        event._entityId
      ).map(
        (target) =>
          target.id
      )
    : [];
};

/*
 * Vrati action targete konkretnog eventa.
 *
 * Event iz PET-a:
 *
 * {
 *   id: 123,
 *   apiBase: "https://petadmin.ddns.net"
 * }
 *
 * Event iz S1:
 *
 * {
 *   id: 123,
 *   apiBase: "https://s1-newadmin.premierbet.me"
 * }
 */
const selectRelatedEventTargets = (
  state: any,
  eventId: number | string
): EventActionTarget[] => {
  const event =
    selectById(
      state,
      eventId
    );

  if (!event) {
    return [];
  }

  if (event.actionTargets?.length) {
    return event.actionTargets.map(
      (
        target: EventActionTarget
      ) => ({
        ...target,

        backendName:
          target.backendName ||
          event.backendName,

        backendUrl:
          target.backendUrl ||
          event.backendUrl,

        apiBase:
          target.apiBase ||
          event.backendUrl,
      })
    );
  }

  return [
    {
      id: event.id,
      backendName:
        event.backendName,
      backendUrl:
        event.backendUrl,
      apiBase:
        event.backendUrl,
    },
  ];
};

const eventManagementReducer =
  eventsSlice.reducer;

const {
  clearState,
  updateEvent,
  updateEventFromSocket,
  updateEventsFromSocket
} = eventsSlice.actions;

export {
  eventManagementReducer,
  updateEvent,
  updateEventFromSocket,
  updateEventsFromSocket,
  clearState,
  fetchEvents,
  selectEventById,
  selectEventByBackend,
  selectAllEvents,
  selectAllEventIds,
  selectRelatedEventIds,
  selectRelatedEventTargets
};